import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoaderBar } from 'components'
import { AppPrimaryButton } from 'elements'
import { useNotification } from 'hooks/toast'
import { GetTeamMemberList } from 'hooks/user'
import {
    ActionPlanRuleService,
    GroupService,
    MasterAnswersService,
    TemplateService,
} from 'utility'
import { CopyRuleModal } from './CopyRuleModal'
import { RuleCard } from './RuleCard'
import { TemplateTicketsPanel } from './TemplateTicketsPanel'
import {
    PageWrap,
    PageContent,
    Breadcrumb,
    PageTitle,
    PageSubtitle,
    SummaryStrip,
    StatCard,
    ToolbarRow,
    SearchInput,
    TipCard,
    TabBar,
    TabButton,
} from './styles'
import {
    computeRuleStats,
    enrichQuestionsWithMasterAnswers,
    enrichRulesWithQuestions,
    enrichRulesWithLookups,
    extractTemplateQuestions,
    getApiErrorMessage,
    normalizeActionPlanTeamList,
    normalizeCategoryList,
    normalizePriorityList,
    normalizeRuleList,
    normalizeTeamGroupList,
    normalizeVendorCompanyList,
    resolveAssigneeTypes,
    ruleMatchesSearch,
} from './utils'
import { normalizeTemplateTicketList, computeEscalationStats } from 'page-components/auto-ticket/utils'

export const ManageRulesPage = ({ templateId, templateName: nameProp }) => {
    const navigate = useNavigate()
    const { successToast, errorToast } = useNotification()
    const toastRef = useRef({ successToast, errorToast })
    toastRef.current = { successToast, errorToast }

    const [loading, setLoading] = useState(true)
    const [baseRules, setBaseRules] = useState([])
    const [ruleLookups, setRuleLookups] = useState(null)
    const [questions, setQuestions] = useState([])
    const [search, setSearch] = useState('')
    const [copyRule, setCopyRule] = useState(null)
    const [copySaving, setCopySaving] = useState(false)
    const [togglingId, setTogglingId] = useState(null)
    const [deletingId, setDeletingId] = useState(null)
    const [ticketCount, setTicketCount] = useState(null)
    const [tickets, setTickets] = useState([])
    const [activeTab, setActiveTab] = useState('rules')
    const [escalationFilter, setEscalationFilter] = useState(null)
    const [templateName, setTemplateName] = useState(nameProp || '')

    const { data: teamMembers } = GetTeamMemberList()

    const rules = useMemo(() => {
        if (!ruleLookups || !baseRules.length) return baseRules
        return enrichRulesWithLookups(baseRules, {
            ...ruleLookups,
            teamMembers: teamMembers || [],
        })
    }, [baseRules, ruleLookups, teamMembers])

    const load = useCallback(async () => {
        setLoading(true)
        try {
            const template = await TemplateService.getTemplateDetails(
                templateId,
            ).catch(() => null)
            if (!template) {
                toastRef.current.errorToast(
                    'Could not load template. Check that you are logged in and the template exists.',
                )
                return
            }

            const numericTemplateId =
                Number(template.Id ?? template.unique_id) || null
            if (!numericTemplateId) {
                toastRef.current.errorToast('Could not resolve template ID.')
                return
            }

            const [
                rulesRes,
                ticketsRes,
                priRes,
                catRes,
                typesRes,
                teamsRes,
                teamGroupRes,
                vendorsRes,
            ] = await Promise.all([
                ActionPlanRuleService.getRulesByTemplate(numericTemplateId),
                ActionPlanRuleService.getTicketsByTemplate(
                    numericTemplateId,
                ).catch(() => null),
                ActionPlanRuleService.getPriorities().catch(() => []),
                ActionPlanRuleService.getCategories().catch(() => []),
                ActionPlanRuleService.getAssignedToTypes().catch(() => []),
                ActionPlanRuleService.getTeams().catch(() => []),
                GroupService.list({ PageNumber: 1, PageSize: 500 }).catch(
                    () => null,
                ),
                ActionPlanRuleService.getVendorCompanies().catch(() => []),
            ])

            const actionPlanTeams = normalizeActionPlanTeamList(teamsRes)
            const teamGroupTeams =
                teamGroupRes && !actionPlanTeams.length
                    ? normalizeTeamGroupList(teamGroupRes)
                    : []

            setRuleLookups({
                priorities: normalizePriorityList(priRes),
                categories: normalizeCategoryList(catRes),
                assignedToTypes: resolveAssigneeTypes(typesRes),
                teams: actionPlanTeams.length
                    ? actionPlanTeams
                    : teamGroupTeams,
                vendorCompanies: normalizeVendorCompanyList(vendorsRes),
            })

            const qs = await enrichQuestionsWithMasterAnswers(
                extractTemplateQuestions(template),
                id => MasterAnswersService.getItems(id),
            )
            const normalized = enrichRulesWithQuestions(
                normalizeRuleList(rulesRes),
                qs,
            )
            setBaseRules(normalized)
            setTemplateName(template.TemplateName || nameProp || '')
            setQuestions(qs)

            if (ticketsRes) {
                const normalizedTickets =
                    normalizeTemplateTicketList(ticketsRes)
                setTickets(normalizedTickets)
                const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
                const recentCount = normalizedTickets.filter(t => {
                    const created = t.createdOn
                    return (
                        created &&
                        new Date(created).getTime() > thirtyDaysAgo
                    )
                }).length
                setTicketCount(recentCount)
            } else {
                setTickets([])
            }
        } catch (err) {
            const msg = getApiErrorMessage(err)
            if (msg.toLowerCase().includes('admin')) {
                toastRef.current.errorToast(
                    'Admin role required to manage auto-ticket rules.',
                )
            } else {
                toastRef.current.errorToast(msg)
            }
        } finally {
            setLoading(false)
        }
    }, [templateId, nameProp])

    useEffect(() => {
        load()
    }, [load])

    const stats = useMemo(
        () => computeRuleStats(rules, ticketCount),
        [rules, ticketCount],
    )
    const escalationStats = useMemo(
        () => computeEscalationStats(tickets),
        [tickets],
    )

    const openEscalationFilter = kind => {
        setEscalationFilter(kind)
        setActiveTab('tickets')
    }

    const filtered = useMemo(
        () => rules.filter(r => ruleMatchesSearch(r, search)),
        [rules, search],
    )

    const handleToggle = async (rule, enabled) => {
        setTogglingId(rule.id)
        try {
            await ActionPlanRuleService.setEnabled(rule.id, enabled)
            setBaseRules(prev =>
                prev.map(r =>
                    r.id === rule.id ? { ...r, isEnabled: enabled } : r,
                ),
            )
            successToast(enabled ? 'Rule enabled' : 'Rule paused')
        } catch (err) {
            errorToast(getApiErrorMessage(err))
        } finally {
            setTogglingId(null)
        }
    }

    const handleDelete = async rule => {
        setDeletingId(rule.id)
        try {
            await ActionPlanRuleService.remove(rule.id)
            setBaseRules(prev => prev.filter(r => r.id !== rule.id))
            successToast('Rule deleted')
        } catch (err) {
            errorToast(getApiErrorMessage(err))
        } finally {
            setDeletingId(null)
        }
    }

    const handleCopyConfirm = async body => {
        if (!copyRule) return
        setCopySaving(true)
        try {
            await ActionPlanRuleService.copyTo(copyRule.id, body)
            successToast('Rule copied to selected questions')
            setCopyRule(null)
            await load()
        } catch (err) {
            errorToast(getApiErrorMessage(err))
        } finally {
            setCopySaving(false)
        }
    }

    const basePath = `/app/templates/${templateId}/auto-rules`

    if (loading) {
        return (
            <PageWrap>
                <LoaderBar content="Loading rules…" />
            </PageWrap>
        )
    }

    return (
        <PageWrap>
            <PageContent>
                <Breadcrumb>
                    <a onClick={() => navigate('/app/templates')}>Templates</a>
                    {' › '}
                    {templateName} › Auto-Ticket Rules
                </Breadcrumb>
                <PageTitle>
                    Auto-Ticket Rules — {templateName}
                </PageTitle>
                <PageSubtitle>
                    All rules currently set up for this template. Click any rule
                    to edit it, or toggle to enable/disable without deleting.
                </PageSubtitle>

                <SummaryStrip>
                    <StatCard $color="purple">
                        <div className="lbl">Total rules</div>
                        <div className="val">{stats.total}</div>
                    </StatCard>
                    <StatCard $color="green">
                        <div className="lbl">Enabled (firing)</div>
                        <div className="val">{stats.enabled}</div>
                    </StatCard>
                    <StatCard $color="gray">
                        <div className="lbl">Disabled (paused)</div>
                        <div className="val">{stats.disabled}</div>
                    </StatCard>
                    <StatCard
                        $color="orange"
                        $clickable
                        onClick={() => openEscalationFilter('escalated')}
                        title="Filter tickets by escalated / overdue (last 30 days)"
                    >
                        <div className="lbl">Escalated · Overdue (30d)</div>
                        <div className="val">
                            {escalationStats.escalated}{' '}
                            <span className="muted">
                                · {escalationStats.overdue}
                            </span>
                        </div>
                    </StatCard>
                </SummaryStrip>

                <TabBar>
                    <TabButton
                        type="button"
                        $active={activeTab === 'rules'}
                        onClick={() => setActiveTab('rules')}
                    >
                        Rules ({rules.length})
                    </TabButton>
                    <TabButton
                        type="button"
                        $active={activeTab === 'tickets'}
                        onClick={() => {
                            setEscalationFilter(null)
                            setActiveTab('tickets')
                        }}
                    >
                        Template tickets ({tickets.length})
                    </TabButton>
                </TabBar>

                {activeTab === 'tickets' ? (
                    <TemplateTicketsPanel
                        tickets={tickets}
                        escalationFilter={escalationFilter}
                    />
                ) : (
                    <>
                <ToolbarRow>
                    <SearchInput
                        type="text"
                        placeholder="Search rules…"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                    <div className="spacer" />
                    <AppPrimaryButton
                        onClick={() => navigate(`${basePath}/create`)}
                    >
                        + Add new rule
                    </AppPrimaryButton>
                </ToolbarRow>

                {filtered.length === 0 ? (
                    <TipCard>
                        <h2>No rules yet</h2>
                        <p>
                            {search
                                ? 'No rules match your search.'
                                : 'Add your first auto-ticket rule to automatically create tickets when auditors pick specific answers.'}
                        </p>
                        {!search && (
                            <div style={{ marginTop: 12 }}>
                                <AppPrimaryButton
                                    onClick={() =>
                                        navigate(`${basePath}/create`)
                                    }
                                >
                                    + Add new rule
                                </AppPrimaryButton>
                            </div>
                        )}
                    </TipCard>
                ) : (
                    filtered.map(rule => (
                        <RuleCard
                            key={rule.id}
                            rule={rule}
                            onToggle={handleToggle}
                            onEdit={r =>
                                navigate(`${basePath}/edit/${r.id}`)
                            }
                            onDelete={handleDelete}
                            onCopy={setCopyRule}
                            toggling={togglingId === rule.id}
                            deleting={deletingId === rule.id}
                        />
                    ))
                )}

                <TipCard style={{ marginTop: 18 }}>
                    <h2>💡 Tip — pausing vs deleting</h2>
                    <p>
                        <b>Pause</b> (toggle off) if you want to stop creating
                        tickets temporarily. <b>Delete</b> if the rule is
                        genuinely wrong. Pausing keeps the rule&apos;s history
                        visible; deleting removes it from the list.
                    </p>
                </TipCard>
                    </>
                )}

                <CopyRuleModal
                    open={Boolean(copyRule)}
                    onClose={() => setCopyRule(null)}
                    rule={copyRule}
                    questions={questions}
                    existingRules={rules}
                    onConfirm={handleCopyConfirm}
                    saving={copySaving}
                />
            </PageContent>
        </PageWrap>
    )
}
